import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { get } from 'lodash';
import { PAGE_SUB_TYPES, PAGE_TYPES } from '@sparefoot/tracking-events';

import withSearchHandler from 'decorators/withSearchHandler';
import './pspPage.scss';
import { Page } from 'components/core/Page';
import { PSPLayout } from 'components/organisms/PSPLayout';
import { ErrorPage as NewFourOhFourPage } from 'pages/ErrorPage/ErrorPage';
import withPageViewTracking from '../../components/core/library/tracking-utils/tracking/withPageViewTracking';
import { getPSPContent, normalizeKeyword } from 'pages/PSPPage/PSPContentMap';
import { getMetadata } from 'pages/PSPPage/utils/getMetadata';
import {
	buildReviewSchema,
	buildPageSchema,
	buildPSPUrl,
	PSP_PAGE_BY_KEY,
} from 'pages/PSPPage/utils/buildSchema';

export const enhance = compose(
	withPageViewTracking((props) => {
		const keyword = get(props.match, 'params.keyword') || props.keyword;
		return PSP_PAGE_BY_KEY[keyword] || PAGE_TYPES.PSP;
	}),
	withSearchHandler,
);

export function PSPPage(props) {
	const { handleSearch } = props;
	const keywordParam = get(props.match, 'params.keyword') || props.keyword;
	const normalizedSlug = normalizeKeyword(keywordParam);
	const pageContent = getPSPContent(keywordParam);

	if (!normalizedSlug || !pageContent) {
		return <NewFourOhFourPage {...props} />;
	}

	const metadata = getMetadata(normalizedSlug);
	const reviewSchema = buildReviewSchema(pageContent);
	const canonicalUrl = buildPSPUrl(keywordParam);
	const heroImageUrl = `https://www.selfstorage.com/images/psp/PSPHero/${normalizedSlug}-hero.webp`;
	const schema = buildPageSchema(
		keywordParam,
		normalizedSlug,
		pageContent.type,
		reviewSchema,
		metadata,
	);
	const pageSchema = JSON.stringify(schema);

	return (
		<Page
			className="psp-page"
			pageType={PAGE_SUB_TYPES.STORAGE_LANDING}
		>
			<Helmet>
				<title>{metadata?.title}</title>
				<link
					rel="canonical"
					href={canonicalUrl}
				/>
				<meta
					name="description"
					content={metadata?.description}
				/>
				<meta
					property="og:title"
					content={metadata?.title}
				/>
				<meta
					property="og:description"
					content={metadata?.description}
				/>
				<meta
					property="og:url"
					content={canonicalUrl}
				/>
				<meta
					property="og:type"
					content="website"
				/>
				<meta
					property="og:image"
					content={heroImageUrl}
				/>
				<meta
					property="og:site_name"
					content="SelfStorage.com"
				/>
				<meta
					name="twitter:card"
					content="summary_large_image"
				/>
				<meta
					name="twitter:site"
					content="@selfstorage"
				/>
				<meta
					name="twitter:title"
					content={metadata?.title}
				/>
				<meta
					name="twitter:description"
					content={metadata?.description}
				/>
				<meta
					name="twitter:image"
					content={heroImageUrl}
				/>
			</Helmet>
			<PSPLayout
				handleSearch={handleSearch}
				content={pageContent}
				pageSchema={pageSchema}
				pageType={pageContent.type}
			/>
		</Page>
	);
}

PSPPage.propTypes = {
	handleSearch: PropTypes.func.isRequired,
	match: PropTypes.object,
	keyword: PropTypes.string,
};

export default enhance(PSPPage);
